import styled, { css, keyframes } from 'styled-components'

import { _S_Normal, _XS_Normal, mainColors, semanticColors } from '@avantstay/backoffice-core'

import { markdownBubbleContent } from './chatMarkdown.styles'

// Shared building blocks for the AI chat side panels (Remy, the Revenue
// "Discuss with Agent" review chat, the execution chat). They define the
// common visual language — message list, bubbles, streaming sheen, input bar —
// so the chats read as the same product surface. Panel-specific styles
// (session pickers, version panels, …) stay with their panel.

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`

export const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 20px 50px;
`

export const MessageRow = styled.div<{ $role: 'human' | 'assistant' }>`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  justify-content: ${({ $role }) => ($role === 'human' ? 'flex-end' : 'flex-start')};
`

// 28px round assistant avatar. Typography only matters when it holds text
// (e.g. the review chat renders a percentage in it); icon-only use is fine.
export const AvatarCircle = styled.div`
  ${_XS_Normal}
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #dbeafe;
  color: #1d4ed8;
  font-weight: 600;
  flex-shrink: 0;
`

export const Bubble = styled.div<{ $role: 'human' | 'assistant'; $streaming?: boolean }>`
  ${_S_Normal}
  position: relative;
  max-width: 78%;
  padding: 10px 14px;
  border-radius: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: hidden;

  ${({ $role }) =>
    $role === 'human'
      ? css`
          background: #2563eb;
          color: ${mainColors.white};
          border-bottom-right-radius: 4px;
        `
      : css`
          background: ${mainColors.neutralLight};
          color: ${mainColors.neutralExtremelyDark2};
          border-bottom-left-radius: 4px;
        `}

  ${({ $streaming }) =>
    $streaming &&
    css`
      min-height: 36px;
    `}

  /* GFM tables + list/paragraph rendering, shared across the AI chats. */
  ${markdownBubbleContent}
`

const sheen = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
`

export const Sheen = styled.span`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.75) 50%,
    transparent 100%
  );
  animation: ${sheen} 1.6s linear infinite;
`

const blink = keyframes`
  0%, 20% { opacity: 0.2; transform: translateY(0); }
  50% { opacity: 1; transform: translateY(-2px); }
  80%, 100% { opacity: 0.2; transform: translateY(0); }
`

export const TypingDots = styled.span`
  display: inline-flex;
  gap: 4px;
  align-items: center;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${mainColors.neutralDark};
    animation: ${blink} 1.2s infinite ease-in-out;
  }
  span:nth-child(2) {
    animation-delay: 0.15s;
  }
  span:nth-child(3) {
    animation-delay: 0.3s;
  }
`

export const ErrorBanner = styled.div`
  ${_XS_Normal}
  padding: 10px 12px;
  border-radius: 6px;
  background: ${semanticColors.failureExtraLight};
  color: ${semanticColors.failureDark};
  border: 1px solid ${semanticColors.failureMedium};
  line-height: 1.4;

  strong {
    font-weight: 700;
    margin-right: 4px;
  }
`

export const InputBar = styled.div`
  padding: 12px 20px 16px;
  border-top: 1px solid ${mainColors.neutralMedium};
  background: ${mainColors.neutralExtraLight};
`

export const InputWrapper = styled.div<{ $disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-end;
  border: 2px solid ${mainColors.neutralMedium};
  border-radius: 8px;
  background: ${({ $disabled }) => ($disabled ? mainColors.neutralExtraLight : mainColors.white)};
  padding: 8px 8px 8px 12px;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: #3b82f6;
  }
`

export const InputArea = styled.textarea`
  ${_S_Normal}
  flex: 1;
  padding: 4px 0;
  border: none;
  background: transparent;
  resize: none;
  font-family: inherit;
  color: ${mainColors.neutralExtremelyDark2};

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${mainColors.neutralExtraDark};
  }

  &:disabled {
    cursor: not-allowed;
  }
`

export const SendButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: 32px;
  height: 32px;
  margin-left: 8px;
  border-radius: 50%;
  border: none;
  background: ${mainColors.accentDark};
  color: ${mainColors.white};
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s,
    transform 0.1s;

  &:hover {
    background: ${mainColors.accentExtraDark};
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    background: ${mainColors.neutralMedium};
    color: ${mainColors.white};
    cursor: not-allowed;
  }
`
